import React, { useEffect, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { useApp, t } from "@/lib/AppContext";
import { api, formatApiError, resolveImg } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";
import { toast } from "sonner";

export default function Reviews() {
  const { content, settings } = useApp();
  const [reviews, setReviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ author: "", text: "", car_name: "", rating: 5 });

  useEffect(() => { api.get("/reviews").then(r => setReviews(r.data || [])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || form.text.trim().length < 10) {
      toast.error("Заполните имя и текст отзыва (минимум 10 символов)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Спасибо! Ваш отзыв отправлен на модерацию");
      setForm({ author: "", text: "", car_name: "", rating: 5 });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Ошибка");
    } finally {
      setSubmitting(false);
    }
  };

  const heroBg = settings?.bg_image_urls?.reviews;
  const heroBgUrl = heroBg ? resolveImg(heroBg) : "";

  return (
    <div data-testid="reviews-page">
      <section
        className="py-16 md:py-24 relative border-b section-on-dark"
        style={{
          borderColor: "var(--color-border)",
          backgroundImage: heroBgUrl ? `linear-gradient(rgba(30,58,95,0.78), rgba(30,58,95,0.88)), url('${heroBgUrl}')` : undefined,
          backgroundColor: heroBgUrl ? undefined : "white",
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: heroBgUrl ? "#fff" : undefined,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <SectionLabel light={!!heroBgUrl}>{t(content, "reviews.label", "Что говорят клиенты")}</SectionLabel>
          <h1 className="section-heading mt-4" style={{ color: heroBgUrl ? "#fff" : undefined }}>{t(content, "reviews.title", "Отзывы покупателей")}</h1>
          <p className="mt-3 max-w-2xl" style={{ color: heroBgUrl ? "rgba(255,255,255,0.85)" : "var(--color-text-secondary)" }}>{t(content, "reviews.subtitle", "Реальные истории наших клиентов")}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-[var(--color-bg-primary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_380px] gap-8">
          {/* List */}
          <div>
            {reviews.length === 0 ? (
              <div className="bg-white rounded-[var(--radius-lg)] p-12 text-center text-[var(--color-text-secondary)]">
                Скоро здесь появятся первые отзывы. Будьте первым!
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reviews.map(rv => (
                  <div key={rv.id} className="car-card p-6" style={{ borderTop: "3px solid var(--color-gold)" }} data-testid={`review-item-${rv.id}`}>
                    <div className="flex gap-1 mb-3">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={16} fill={i <= rv.rating ? "var(--color-gold)" : "transparent"} stroke="var(--color-gold)" />
                      ))}
                    </div>
                    <p className="text-[var(--color-text-primary)] mb-4 leading-relaxed">{rv.text}</p>
                    <div className="text-sm">
                      <div className="font-headings font-bold">{rv.author}</div>
                      {rv.car_name && <div className="text-[var(--color-text-secondary)] text-xs mt-1">{rv.car_name}</div>}
                      {rv.published_at && (
                        <div className="text-[var(--color-text-secondary)] text-xs mt-1">
                          {new Date(rv.published_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form */}
          <aside className="bg-white rounded-[var(--radius-lg)] p-6 border-t-[3px] h-fit" style={{ borderColor: "var(--color-primary)", boxShadow: "var(--shadow-card)" }}>
            <h3 className="font-headings font-bold text-xl mb-4">Оставить отзыв</h3>
            <form onSubmit={submit} className="space-y-3" data-testid="review-form">
              <input className="field-input" placeholder="Ваше имя" value={form.author} onChange={e => setForm(f => ({...f, author: e.target.value}))} required data-testid="review-author" />
              <input className="field-input" placeholder="Купленный автомобиль (необязательно)" value={form.car_name} onChange={e => setForm(f => ({...f, car_name: e.target.value}))} />
              <textarea className="field-input" rows={4} placeholder="Ваш отзыв" value={form.text} onChange={e => setForm(f => ({...f, text: e.target.value}))} required data-testid="review-text" />
              <div>
                <div className="text-xs font-accent uppercase tracking-widest text-[var(--color-text-secondary)] mb-2">Оценка</div>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <button type="button" key={i} onClick={() => setForm(f => ({...f, rating: i}))} aria-label={`${i} звёзд`}>
                      <Star size={26} fill={i <= form.rating ? "var(--color-gold)" : "transparent"} stroke="var(--color-gold)" />
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={submitting || !form.author.trim() || form.text.trim().length < 10} className="btn-primary-cta w-full" data-testid="review-submit">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : "Отправить отзыв"}
              </button>
              <p className="text-xs text-[var(--color-text-secondary)] mt-2">Отзыв появится на сайте после модерации</p>
            </form>
          </aside>
        </div>
      </section>
    </div>
  );
}
